import { motion } from "framer-motion";
import { STAGE_NAMES } from "@/lib/skincare";

// SVG plant that grows through 7 stages. Pure SVG + framer-motion.
export default function PlantHero({ stage = 0, dayNum, streak, dose }) {
  const stageName = STAGE_NAMES[Math.min(stage, 10)];

  return (
    <div
      data-testid="plant-hero-container"
      className="relative pt-10 pb-6 px-6 bg-gradient-to-b from-[#E9F0E8] via-[#EFF0E5] to-transparent rounded-b-[44px] overflow-hidden"
    >
      {/* Soft floating blobs */}
      <div className="absolute -top-8 -left-8 w-40 h-40 rounded-full bg-[#CDE0D1]/40 blur-3xl" />
      <div className="absolute top-20 -right-10 w-32 h-32 rounded-full bg-[#FADBD8]/50 blur-3xl" />

      <div className="relative flex items-start justify-between mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#6B7A70]">
            Day {dayNum > 0 ? dayNum : "—"} of 60
          </div>
          <div className="font-heading text-2xl font-semibold text-[#2A332C] mt-1">
            {stageName}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#6B7A70]">Streak</div>
          <div className="font-heading text-2xl font-semibold text-[#4A6B53] mt-1" data-testid="streak-count">
            {streak}🔥
          </div>
        </div>
      </div>

      {/* Plant SVG */}
      <div className="relative h-56 flex items-end justify-center">
        <svg viewBox="0 0 240 240" className="w-56 h-56" fill="none">
          {/* Pot */}
          <motion.path
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            d="M70 190 L170 190 L160 225 L80 225 Z"
            fill="#CC7A6B"
          />
          <ellipse cx="120" cy="190" rx="50" ry="6" fill="#A85F50" />

          {/* Soil */}
          <ellipse cx="120" cy="187" rx="46" ry="5" fill="#3D2C25" />

          {/* Stage 0: seed only (no plant) */}
          {stage === 0 && (
            <motion.circle
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              cx="120"
              cy="185"
              r="4"
              fill="#5C4A38"
            />
          )}

          {/* Stem (stages 1+) */}
          {stage >= 1 && (
            <motion.path
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              d={`M120 187 Q ${118 - stage} ${165 - stage * 10}, 120 ${
                160 - stage * 12
              }`}
              stroke="#4A6B53"
              strokeWidth="3"
              strokeLinecap="round"
            />
          )}

          {/* Leaves layered by stage */}
          {stage >= 2 && (
            <motion.path
              initial={{ scale: 0, originX: "120px", originY: "170px" }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
              d="M120 170 Q 100 162, 92 150 Q 105 152, 120 170 Z"
              fill="#6B9173"
            />
          )}
          {stage >= 2 && (
            <motion.path
              initial={{ scale: 0, originX: "120px", originY: "170px" }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: "spring" }}
              d="M120 168 Q 140 160, 150 148 Q 137 152, 120 168 Z"
              fill="#7BA384"
            />
          )}

          {stage >= 3 && (
            <motion.path
              initial={{ scale: 0, originX: "120px", originY: "150px" }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
              d="M120 148 Q 95 138, 84 122 Q 102 128, 120 148 Z"
              fill="#5C8D6D"
            />
          )}
          {stage >= 3 && (
            <motion.path
              initial={{ scale: 0, originX: "120px", originY: "150px" }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.6, type: "spring" }}
              d="M120 146 Q 146 136, 158 120 Q 138 128, 120 146 Z"
              fill="#6B9173"
            />
          )}

          {stage >= 4 && (
            <motion.path
              initial={{ scale: 0, originX: "120px", originY: "130px" }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.7, type: "spring" }}
              d="M120 128 Q 92 116, 78 96 Q 100 106, 120 128 Z"
              fill="#4A6B53"
            />
          )}
          {stage >= 4 && (
            <motion.path
              initial={{ scale: 0, originX: "120px", originY: "130px" }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.8, type: "spring" }}
              d="M120 126 Q 150 114, 162 92 Q 142 106, 120 126 Z"
              fill="#5C8D6D"
            />
          )}

          {/* Flower buds (stage 5+) */}
          {stage >= 5 &&
            [
              [120, 80],
              [100, 92],
              [140, 92],
            ].map(([cx, cy], i) => (
              <motion.g
                key={i}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.9 + i * 0.1, type: "spring" }}
              >
                <circle cx={cx} cy={cy} r="7" fill="#FADBD8" />
                <circle cx={cx} cy={cy} r="3" fill="#CC7A6B" />
              </motion.g>
            ))}

          {/* Stage 6: extra flowers */}
          {stage >= 6 &&
            [
              [80, 72],
              [160, 72],
              [120, 60],
            ].map(([cx, cy], i) => (
              <motion.g
                key={i}
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 1.1 + i * 0.1, type: "spring" }}
              >
                <circle cx={cx} cy={cy} r="9" fill="#F4C2B4" />
                <circle cx={cx} cy={cy} r="4" fill="#CC7A6B" />
              </motion.g>
            ))}

          {/* Stage 7: wide-spread leaves higher up */}
          {stage >= 7 && (
            <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.4, type: "spring" }}>
              <path d="M120 110 Q 80 95, 65 75 Q 90 82, 120 110 Z" fill="#4A6B53" />
              <path d="M120 108 Q 162 93, 178 73 Q 152 82, 120 108 Z" fill="#5C8D6D" />
            </motion.g>
          )}

          {/* Stage 8: golden berry clusters */}
          {stage >= 8 &&
            [[105, 68], [135, 68], [120, 52]].map(([cx, cy], i) => (
              <motion.g key={i} initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ delay: 0.5 + i * 0.1, type: "spring" }}>
                <circle cx={cx - 4} cy={cy} r="3.5" fill="#E6B97A" />
                <circle cx={cx + 4} cy={cy} r="3.5" fill="#D4A45A" />
                <circle cx={cx} cy={cy - 5} r="3.5" fill="#E6B97A" />
              </motion.g>
            ))}

          {/* Stage 9: crown flower at the very top */}
          {stage >= 9 && (
            <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ delay: 0.6, type: "spring" }}>
              {[0,60,120,180,240,300].map((deg, i) => (
                <motion.ellipse key={i}
                  cx={120 + 12 * Math.cos((deg * Math.PI) / 180)}
                  cy={36 + 8 * Math.sin((deg * Math.PI) / 180)}
                  rx="7" ry="5"
                  fill="#FADBD8"
                  transform={`rotate(${deg} ${120 + 12 * Math.cos((deg * Math.PI) / 180)} ${36 + 8 * Math.sin((deg * Math.PI) / 180)})`}
                />
              ))}
              <circle cx="120" cy="36" r="6" fill="#CC7A6B" />
              <circle cx="120" cy="36" r="2.5" fill="#FFF" />
            </motion.g>
          )}

          {/* Stage 10: golden halo ring */}
          {stage >= 10 && (
            <motion.circle
              cx="120" cy="36" r="22"
              fill="none"
              stroke="#E6B97A"
              strokeWidth="2"
              strokeDasharray="4 3"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: [0.4, 0.9, 0.4], scale: 1 }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            />
          )}

          {/* Sparkles: start at stage 4, more at 7+ */}
          {stage >= 4 &&
            [
              [60, 60], [180, 70], [40, 130], [200, 140],
              ...(stage >= 7 ? [[30, 80], [210, 90], [75, 40], [165, 40]] : []),
              ...(stage >= 10 ? [[20, 165], [220, 170], [100, 18], [140, 18]] : []),
            ].map(([cx, cy], i) => (
              <motion.circle
                key={i}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: [0, 1, 0], scale: [0, 1.5, 0] }}
                transition={{
                  delay: 1.4 + i * 0.12,
                  duration: 2.5,
                  repeat: Infinity,
                  repeatDelay: 1.2,
                }}
                cx={cx} cy={cy} r="2"
                fill={i >= 8 ? "#CC7A6B" : "#E6B97A"}
              />
            ))}
        </svg>
      </div>

      {dose && (
        <div className="relative flex items-center justify-center gap-2 mt-2">
          <div className="px-4 py-1.5 bg-white/80 backdrop-blur rounded-full border border-[#E9EFEA] text-sm">
            <span className="text-[#6B7A70]">Tonight&rsquo;s Isotane: </span>
            <span className="font-semibold text-[#2A332C]" data-testid="isotane-dose">{dose}</span>
          </div>
        </div>
      )}
    </div>
  );
}
